import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Lightning, Crown, ArrowRight, Sparkle, PaperPlaneTilt, FileText, FilePdf, DownloadSimple, Presentation } from '@phosphor-icons/react';
import HelpAnchor from '../components/HelpAnchor';
import { useAuth } from '../context/AuthContext';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import CurrencySwitcher, { guessCurrency, convertFromAud, formatMoney, CURRENCY_FETCH } from '../components/CurrencySwitcher';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public marketing pricing page. Six V2 tiers + add-on showcase.
 *
 * Logic:
 *  • Logged-out → CTAs route to /signup with the chosen tier in query.
 *  • Logged-in & on a different tier → CTAs route to /billing for Stripe upgrade.
 *  • Logged-in & on this tier → CTA disabled with "Current plan" pill.
 */
const ORDER = ['pallets', 'starter', 'grow', 'core', 'scale', 'business', 'enterprise'];

const TIER_BLURB = {
  pallets:    { hook: 'CHEP + Loscam tracking. No full WMS needed.', for: '3PLs & warehouses that already run a WMS but want pooler-loss cover.' },
  starter:    { hook: 'Run your shop without spreadsheets.', for: 'Solo Etsy / Shopify sellers, hobby-turned-business.' },
  grow:       { hook: 'Single-channel SMB, ready to scale.', for: '1–3 staff, 500–5k orders/yr, 1 warehouse.' },
  core:       { hook: 'The multi-channel ops sweet spot.',   for: 'Cin7-feature parity at 1/6th the price. Most popular.' },
  scale:      { hook: 'Built for growing 3PLs.',             for: '20–50 staff, 20k–100k orders/yr, multi-warehouse.' },
  business:   { hook: 'Manhattan brain. Shopify UX.',        for: 'Mid-market brands with audit + compliance needs.' },
  enterprise: { hook: 'Your brand. Your customers. Our engine.', for: 'White-label · large 3PLs · franchise networks.' },
};

const FEATURE_LABELS = {
  pallets: 'CHEP + Loscam tracking', pallet_investigator: 'Loss Investigator',
  pallet_reconciliation: 'Pooler reconciliation', pallet_pooler_sync: 'Auto pooler sync',
  pallet_analytics: 'Pallet analytics + trust scores',
  merchants: 'Merchant / customer directory', warehouses: 'Warehouse scoping',
  integrations: 'Integrations & webhooks', audit_log: 'Audit log',
  inventory: 'Inventory tracking', locations: 'Bin/zone locations', orders: 'Order management',
  picking: 'Pick lists', packing: 'Pack lists', receiving: 'Receiving',
  shipping: 'Shipping labels', reports: 'Reports', barcode: 'Barcode scanning',
  bom: 'BOM & manufacturing', manufacturing: 'Production orders',
  multi_warehouse: 'Multi-warehouse', transfers: 'Stock transfers',
  forecasting: 'Demand forecasting', analytics: 'Advanced analytics',
  warehouse_kpis: 'Warehouse KPIs', leaderboard: 'Team leaderboard',
  cycle_counts: 'Cycle counting', quarantine: 'Quarantine + QA hold',
  alerts: 'Email + SMS alerts', csv_import: 'CSV import / export',
  custom_reports: 'Custom reports', location_reports: 'Location reports',
  ai_pdf_import: 'AI PDF import',
  dispatch: 'Dispatch & loading', containers: 'FCL/LCL containers',
  driver_portal: 'Driver portal', live_map: 'Live driver map',
  returns: 'Returns + RMAs', sku_velocity: 'SKU velocity reports',
  bulk_labels: 'Bulk label printing',
  saml_sso: 'SAML SSO', '2fa': 'Two-factor auth',
  audit_log: 'Audit log', compliance: 'Compliance reports',
  retention: 'Data retention policies', broadcast: 'Broadcast messaging',
  release_notes: 'Release notes', voice_pick: 'Voice picking',
  web_push: 'Web push notifications', api_access: 'REST API access',
  carrier_portal: 'Carrier portal', white_label: 'White-label branding',
  webhook_integrations: 'Webhook integrations',
  // Add-on features (iter 135)
  pallet_tracking: 'CHEP & Loscam pallet tracking',
  customer_pricing: 'Custom customer price lists',
  '3pl_billing': '3PL billing & rate cards',
  margins: 'Margin dashboard',
  pricing_actions: 'Apply / Undo pricing floor',
};

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [billCycle, setBillCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [fxData, setFxData] = useState(null);
  const [currency, setCurrency] = useState('AUD');

  useEffect(() => {
    axios.get(`${API}/api/plans`).then((r) => {
      const filtered = (r.data || []).filter((p) => p.key !== 'trial');
      filtered.sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
      setPlans(filtered);
    }).catch(() => setPlans([]));
    axios.get(`${API}/api/billing/addons/catalog`).then((r) => setAddons(r.data || [])).catch(() => setAddons([]));
    // Localised pricing — pull FX rates then pick a default currency.
    // Priority: persisted user choice > browser locale mapping > AUD.
    CURRENCY_FETCH().then((data) => {
      setFxData(data);
      let saved = null;
      try { saved = localStorage.getItem('stockaris_currency'); } catch (_) { /* ignore */ }
      const supported = data?.supported || ['AUD'];
      if (saved && supported.includes(saved)) {
        setCurrency(saved);
      } else {
        setCurrency(guessCurrency(supported));
      }
    }).catch(() => { /* fx endpoint down — keep AUD */ });
  }, []);

  const startCheckout = (planKey) => {
    if (!user) { navigate(`/signup?plan=${planKey}&cycle=${billCycle}`); return; }
    navigate(`/billing?upgrade=${planKey}&cycle=${billCycle}`);
  };

  const formatPrice = (n) => {
    // Custom-priced tiers (price_monthly_usd === null) render with their own
    // Custom badge — return null here so the JSX can branch.
    if (n == null) return null;
    if (billCycle === 'annual') {
      const annual = Math.round(n * 10); // 2 months free for annual
      return `A$${annual.toLocaleString()}`;
    }
    return `A$${n.toLocaleString()}`;
  };
  // Return the localised equivalent (e.g. "€59" or "¥475") if currency != AUD
  // and fxData is loaded. Empty string otherwise so callers can render blank.
  const formatLocalisedPrice = (n) => {
    if (n == null || currency === 'AUD' || !fxData) return '';
    const audAmount = billCycle === 'annual' ? n * 10 : n;
    const converted = convertFromAud(audAmount, currency, fxData);
    if (converted == null) return '';
    return formatMoney(converted, currency, fxData);
  };
  const cycleLabel = billCycle === 'annual' ? '/yr' : '/mo';

  // State for the Quote Request modal — used by the Enterprise CTA below
  // and any future custom-priced tier.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteFeatures, setQuoteFeatures] = useState([]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="pricing-page">
      {/* Top nav */}
      <nav className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-brand" /> STOCKARIS
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/" className="text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground" data-testid="pricing-back-to-app">Back to app</Link>
            ) : (
              <>
                <Link to="/login" className="text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground">Sign in</Link>
                <Link to="/signup">
                  <Button size="sm" className="bg-brand hover:bg-brand/90 rounded-none" data-testid="pricing-nav-signup">Sign up</Button>
                </Link>
                <Link to="/request-demo">
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="pricing-nav-demo">Request a demo</Button>
                </Link>
              </>
            )}
            <CurrencySwitcher value={currency} onChange={setCurrency} fxData={fxData} />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-20 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-brand mb-4">Pricing</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
          One platform. Every tier.<br />
          <span className="text-brand">No quote calls.</span>
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto mb-8">
          From A$99/mo Etsy seller to custom-priced white-label 3PL. Pick the tier that fits, layer add-ons à la carte, upgrade or downgrade any time. All prices in AUD.
        </p>

        {/* Billing cycle toggle */}
        <div className="inline-flex items-center bg-muted border border-border p-1" data-testid="pricing-cycle-toggle">
          <button
            onClick={() => setBillCycle('monthly')}
            data-testid="cycle-monthly"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${billCycle === 'monthly' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
          >Monthly</button>
          <button
            onClick={() => setBillCycle('annual')}
            data-testid="cycle-annual"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${billCycle === 'annual' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
          >
            Annual <span className="ml-1 text-emerald-600">save 17%</span>
          </button>
        </div>
      </header>

      {/* Plan grid */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pricing-plan-grid">
          {plans.map((p) => {
            const isCurrent = user && user.company?.plan_key === p.key;
            const isMostPopular = p.key === 'core';
            const blurb = TIER_BLURB[p.key] || {};
            return (
              <div
                key={p.key}
                data-testid={`pricing-plan-${p.key}`}
                className={`relative border p-5 flex flex-col ${
                  isMostPopular ? 'border-brand bg-brand/5 ring-2 ring-brand' : 'border-border'
                }`}
              >
                {isMostPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1">
                    Most Popular
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{p.name}</div>
                <div className="mt-2 mb-1">
                  {p.price_monthly_usd == null ? (
                    <>
                      <span className="text-2xl font-bold">Custom</span>
                      <span className="text-xs text-muted-foreground font-mono ml-2">pricing</span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold tabular-nums">{formatPrice(p.price_monthly_usd)}</span>
                      <span className="text-xs text-muted-foreground font-mono ml-1">{cycleLabel}</span>
                      <div className="text-[9px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5">GST inc.</div>
                      {currency !== 'AUD' && formatLocalisedPrice(p.price_monthly_usd) && (
                        <div
                          className="text-[10px] text-muted-foreground font-mono mt-0.5"
                          data-testid={`localised-price-${p.key}`}
                        >
                          ≈ {formatLocalisedPrice(p.price_monthly_usd)}{cycleLabel} · Billed in AUD
                        </div>
                      )}
                    </>
                  )}
                </div>
                <p className="text-[11px] text-foreground font-medium mb-1">{blurb.hook}</p>
                <p className="text-[10px] text-muted-foreground mb-3 leading-tight">{blurb.for}</p>

                <ul className="space-y-1 text-[11px] flex-1 mb-4">
                  <li className="flex items-start gap-1.5">
                    <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                    <span>
                      {p.limits?.users ?? '∞'} users
                      {(p.limits?.skus ?? null) !== null && p.limits.skus > 0 && (
                        <> · {p.limits.skus.toLocaleString()} SKUs</>
                      )}
                      {p.key === 'pallets' && <> · Pallet-only</>}
                    </span>
                  </li>
                  {!(p.limits?.orders_per_month === 0) && (
                    <li className="flex items-start gap-1.5">
                      <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                      <span>{(p.limits?.orders_per_month ?? null) === null ? 'Unlimited' : p.limits.orders_per_month.toLocaleString()} orders/mo</span>
                    </li>
                  )}
                  <li className="flex items-start gap-1.5">
                    <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                    <span>{p.limits?.warehouses ?? '∞'} warehouse{(p.limits?.warehouses ?? 2) === 1 ? '' : 's'}</span>
                  </li>
                  {(p.features || []).slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                      <span>{FEATURE_LABELS[f] || f.replace(/_/g, ' ')}</span>
                    </li>
                  ))}
                  {(p.features || []).length > 5 && (
                    <li className="text-[10px] text-muted-foreground pl-3">+ {p.features.length - 5} more</li>
                  )}
                </ul>

                {isCurrent ? (
                  <Button disabled className="w-full rounded-none bg-muted text-muted-foreground cursor-not-allowed" data-testid={`plan-current-${p.key}`}>
                    Current plan
                  </Button>
                ) : p.price_monthly_usd == null ? (
                  // Custom-priced tier — open the quote request dialog
                  // instead of routing to checkout.
                  <Button
                    onClick={() => { setQuoteFeatures(p.features || []); setQuoteOpen(true); }}
                    data-testid={`plan-cta-${p.key}`}
                    className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <PaperPlaneTilt size={14} weight="bold" className="mr-1" /> Request a quote
                  </Button>
                ) : (
                  <Button
                    onClick={() => startCheckout(p.key)}
                    data-testid={`plan-cta-${p.key}`}
                    className={`w-full rounded-none ${isMostPopular ? 'bg-brand hover:bg-brand/90 text-white' : 'bg-foreground hover:bg-foreground/90 text-background'}`}
                  >
                    {user ? 'Upgrade' : 'Sign up'} <ArrowRight size={14} className="ml-1" weight="bold" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Add-ons row */}
      {addons.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="pricing-addons-section">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-brand mb-2">Add-ons</p>
            <h2 className="text-2xl font-bold tracking-tight mb-1">Layer extras à la carte</h2>
            <p className="text-sm text-muted-foreground">Pay only for what you use. Cancel any time.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {addons.map((a) => (
              <div key={a.key} data-testid={`pricing-addon-${a.key}`} className="border border-border p-4 hover:border-brand transition-colors">
                <div className="text-xs font-bold tracking-tight">{a.name}</div>
                <div className="mt-1 mb-2">
                  <span className="text-xl font-bold tabular-nums">A${a.price_monthly_usd}</span>
                  <span className="text-[10px] text-muted-foreground font-mono ml-1">/mo</span>
                  <span className="text-[9px] text-muted-foreground font-mono ml-2">GST inc.</span>
                  {currency !== 'AUD' && fxData && (
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5" data-testid={`localised-price-addon-${a.key}`}>
                      ≈ {formatMoney(convertFromAud(a.price_monthly_usd, currency, fxData), currency, fxData)}/mo · Billed in AUD
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">{a.tagline}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Resources — public downloads for procurement decks + cold outreach */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="pricing-resources-section">
        <div className="text-center mb-8">
          <div className="text-[11px] font-black uppercase tracking-widest text-brand mb-1">Resources</div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center justify-center gap-3">
            Send this to your team
            <HelpAnchor articleId="marketing-resources" label="Where to grab & share these" />
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Everything you need to pitch Stockaris internally — a full capabilities dossier, a printable one-pager, and an 8-slide investor deck with ANZ TAM math.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {/* Capabilities Word doc */}
          <a
            href={`${API}/api/marketing/capabilities.docx`}
            className="group border-2 border-brand/20 hover:border-brand p-5 bg-card transition-colors flex items-start gap-4"
            data-testid="resource-capabilities-docx"
          >
            <div className="w-12 h-12 bg-brand/10 group-hover:bg-brand flex items-center justify-center flex-shrink-0 transition-colors">
              <FileText size={24} weight="fill" className="text-brand group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-brand mb-1">
                Word · 27 sections
              </div>
              <div className="text-lg font-bold leading-tight">
                Full Capabilities Guide
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Every module, every workflow, every differentiator — formatted for procurement and stakeholder review.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand group-hover:gap-2 transition-all">
                <DownloadSimple size={12} weight="bold" /> Download .docx
              </div>
            </div>
          </a>

          {/* Capabilities PDF (companion) */}
          <a
            href={`${API}/api/marketing/capabilities.pdf`}
            className="group border-2 border-brand/20 hover:border-brand p-5 bg-card transition-colors flex items-start gap-4"
            data-testid="resource-capabilities-pdf"
          >
            <div className="w-12 h-12 bg-brand/10 group-hover:bg-brand flex items-center justify-center flex-shrink-0 transition-colors">
              <FilePdf size={24} weight="fill" className="text-brand group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-brand mb-1">
                PDF · 27 sections
              </div>
              <div className="text-lg font-bold leading-tight">
                Capabilities Guide (PDF)
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Same dossier as the Word doc — including TMS, timezone-aware windows, and Competition Demo Mode — laid out for print + email.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand group-hover:gap-2 transition-all">
                <DownloadSimple size={12} weight="bold" /> Download .pdf
              </div>
            </div>
          </a>

          {/* One-pager PDF */}
          <a
            href={`${API}/api/marketing/onepager.pdf`}
            className="group border-2 border-amber-500/20 hover:border-amber-500 p-5 bg-card transition-colors flex items-start gap-4"
            data-testid="resource-onepager-pdf"
          >
            <div className="w-12 h-12 bg-amber-500/10 group-hover:bg-amber-500 flex items-center justify-center flex-shrink-0 transition-colors">
              <FilePdf size={24} weight="fill" className="text-amber-600 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">
                PDF · 1 page
              </div>
              <div className="text-lg font-bold leading-tight">
                Cold-Outreach One-Pager
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Punchy printable summary — signature differentiators, key numbers, and CTA. Perfect for LinkedIn DMs and email attachments.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-amber-700 group-hover:gap-2 transition-all">
                <DownloadSimple size={12} weight="bold" /> Download .pdf
              </div>
            </div>
          </a>

          {/* Investor / TAM pitch deck PDF */}
          <a
            href={`${API}/api/marketing/pitch-deck.pdf`}
            className="group border-2 border-emerald-600/20 hover:border-emerald-600 p-5 bg-card transition-colors flex items-start gap-4"
            data-testid="resource-pitch-deck-pdf"
          >
            <div className="w-12 h-12 bg-emerald-600/10 group-hover:bg-emerald-600 flex items-center justify-center flex-shrink-0 transition-colors">
              <Presentation size={24} weight="fill" className="text-emerald-700 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">
                PDF · 8 slides
              </div>
              <div className="text-lg font-bold leading-tight">
                Investor + Enterprise Deck
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Landscape A4. Problem, solution, real product screenshots, ANZ TAM math (~AUD 92.6M ARR ceiling), pricing tiers, and a printable "Talk to sales" form.
              </p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 group-hover:gap-2 transition-all">
                <DownloadSimple size={12} weight="bold" /> Download .pdf
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-muted border-y border-border py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight mb-3">
            Pick a plan in 30 seconds. <span className="text-brand">Cancel any time.</span>
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Or book a 20-minute walkthrough first — we'll show how Stockaris fits your warehouse before you ever touch a card.
          </p>
          {!user && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/signup">
                <Button size="lg" className="bg-brand hover:bg-brand/90 rounded-none" data-testid="pricing-bottom-cta-signup">
                  <Lightning size={16} weight="fill" className="mr-2" /> Sign up & pay
                </Button>
              </Link>
              <Link to="/request-demo">
                <Button size="lg" variant="outline" className="rounded-none" data-testid="pricing-bottom-cta-demo">
                  Request a demo first
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="rounded-none">Sign in</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© 2026 Stockaris · Warehouse Management System</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/legal/sla" className="hover:text-foreground">SLA</Link>
          </div>
        </div>
      </footer>

      {/* Quote-request modal — opened from any custom-priced tier CTA */}
      <QuoteRequestDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        prefillFeatures={quoteFeatures}
      />
    </div>
  );
}
